import { takeLatest, spawn, call, put, select, all } from 'redux-saga/effects'

import actions from 'redux/action'
import { INIT_APP, RACE_AVAILABLE_ORIGIN } from 'redux/constant/app'
import {
  fetchNavigationContent,
  fetchVideoTagList,
  fetchProducerList,
  fetchAvailableOrigin,
  fetchAdList,
  fetchRegionList,
  fetchAnnouncement,
  fetchBinFile,
  fetchStation,
  fetchVideoTopicList,
  fetchGameUserStatus,
  exchangeTemporaryToken,
  fetchMemberAnnounceList,
  SchemaMemberAnnounceShow,
  SchemaOholoMaterialShow,
} from 'api'

import { AdvertisementType } from 'constant/advertisement'
import { getStationId } from 'utils/settings'
import { DEFAULT_ANALYTIC_USER_ID, fetchUser, getCachedShowId } from './user'
import { URL_QUERY_SKIP_LOGIN_TOKEN, URL_QUERY_TEMP_TOKEN } from 'constant/routes'
import { setAnalyticReady, setAnalyticUser } from 'hooks/useAnalytics'
import { ApiError } from 'utils/apiError'
import { processURL } from 'utils/debugUtil'
import { selectCurrentSplashAd, selectPictureOrigin } from 'redux/selector/app'
import { AnnouncementType } from 'constant/announcement'
import { getMetadataJSONWithUrl, precachePreloadConfig } from 'utils/id-util/update-util'
import { isInWhiteListRoute } from 'utils/selfPtotect'
import { fetchDeviceQrcodeWithCache } from 'api/qrcode'
import { PRELOAD_DATA_LOCAL_STORAGE_KEY } from 'constant/common'
import { filterInvalidDomains, getFastestOriginWithDataVal } from 'utils/fastestSpeed'

const sleep = (timeMs: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, timeMs)
  })
}

const BASE_WAIT_TIME_MS = 5000
const TIME_SCALER = 1.5
const MAX_WAIT_TIME = 30000
const WAIT_TIME_RANGE = 3000
const MAX_RETRY_ATTEMPTS = 3
const PER_REQUEST_TIMEOUT_MS = 8000
const ROUTE_ERROR_RETRY_MESSAGES = ['验证站点数据中...', '连线中...']
const DATA_ERROR_RETRY_MESSAGES = ['获取站点资料中...', '连线中...']

export const CACHED_AD_ITEM_KEY = 'CAIK'

export const mapToUserError = (e: any): any => {
  let userError = e
  if (e && /network error/i.test(String(e.message))) {
    userError = Object.assign(new Error('加载时间较长时，可尝试切换\n其他线路更快进入平台❤️(1)'), { title: '温馨提示' })
    if (process.env.NODE_ENV === 'development') {
      console.log('mapped error', e)
    }
  }
  return userError
}

// the build-in join will ate the error and kill parent saga,
// so we need to manually await the task and throw the error
const joinWithProperErrorHandling = (task: any): any => {
  return call(async () => {
    try {
      const d = await task.toPromise()
      return d
    } catch (err) {
      throw err
    }
  })
}

function* runWithRetry(generator: () => Generator): Generator {
  let hasError = false
  let retryDataTime = 0

  let waitTime = BASE_WAIT_TIME_MS

  do {
    try {
      const result = yield* generator()
      hasError = false
      return result
    } catch (err) {
      hasError = true
      if (ApiError.isMaintenanceError(err)) {
        console.log('System is in maintenance, aborting')
        // do not capture MaintenanceError
        throw err
      }
      if (ApiError.isForbidden(err)) {
        // do not capture MaintenanceError
        throw err
      }

      yield put(
        actions.updateRouteDetectMessage(DATA_ERROR_RETRY_MESSAGES[retryDataTime % DATA_ERROR_RETRY_MESSAGES.length])
      )

      if (retryDataTime >= MAX_RETRY_ATTEMPTS) {
        throw err
      }

      yield sleep(waitTime + (Math.random() * 2 - 1) * WAIT_TIME_RANGE)
      waitTime = Math.min(waitTime * TIME_SCALER, MAX_WAIT_TIME)
      retryDataTime++
    }
  } while (hasError)
}

export function* showInvalidTempTokenPage(): Generator {
  yield put(
    actions.updateFullPageError({
      visible: true,
      error: {
        title: '连结已失效',
        message: '请重新回到App继续使用',
        hideButton: true,
      },
    })
  )
}

// 遊戲內頁面假登入
function* fakeInitForGame(tokenJSON: any): Generator {
  if (typeof tokenJSON == 'string') {
    tokenJSON = JSON.parse(tokenJSON)
  }
  setAnalyticUser(getCachedShowId() ?? DEFAULT_ANALYTIC_USER_ID)
  yield put(actions.updateTokenInfo(tokenJSON))
  // yield put(actions.updateReadyToShowSplashAd(true))
  yield skipAd()
  // make game page accessible
  yield put(actions.updateUser({ id: 'user_id_for_game' } as unknown as Parameters<typeof actions.updateUser>[0]))
}

// 獨立影片頁面假登入
function* fakeInitForBrowserVideo(tempToken: string): Generator {
  try {
    const realToken: any = yield call(exchangeTemporaryToken, tempToken)
    yield put(actions.updateTokenInfoWithoutPersist(realToken))
    yield put(actions.updateIsTempLogin(true))
    yield skipAd()
  } catch (err) {
    yield showInvalidTempTokenPage()
    throw err
  }

  setAnalyticUser(getCachedShowId() ?? DEFAULT_ANALYTIC_USER_ID)
  yield put(actions.updateReadyToEnter(true))
  yield put(actions.updateViewedAdvertisement(true))
  yield put(actions.updateViewedMobileConfigUpdate(true))
  yield put(actions.updateIsShowingLaunchSplash(false))
  yield put(actions.updateUser({ id: 'user_id_for_video' } as unknown as Parameters<typeof actions.updateUser>[0]))

  // clear temp token from url
  const url = new URL(window.location.href)
  url.searchParams.delete(URL_QUERY_TEMP_TOKEN)
  window.history.replaceState({}, document.title, url.toString())
}

function* skipAd(): Generator {
  yield put(actions.updateReadyToEnter(true))
  yield put(actions.updateViewedAdvertisement(true))
  yield put(actions.updateViewedMobileConfigUpdate(true))
  yield put(actions.updateIsShowingLaunchSplash(false))
  yield put(actions.updateReadyToShowSplashAd(true))
}

function* init(): Generator {
  yield console.log('App init')

  // FIXME: workaround to open new page
  const url = new URL(window.location.href)
  if (url.searchParams.get(URL_QUERY_SKIP_LOGIN_TOKEN) != null) {
    yield* fakeInitForGame(url.searchParams.get(URL_QUERY_SKIP_LOGIN_TOKEN))
    setAnalyticReady()
    return
  }
  const isBrowserVideoPage = isInWhiteListRoute()
  if (url.searchParams.get(URL_QUERY_TEMP_TOKEN) != null) {
    yield* fakeInitForBrowserVideo(url.searchParams.get(URL_QUERY_TEMP_TOKEN) as string)
  }

  // 瀏覽器網頁不顯示廣告
  if (isBrowserVideoPage) {
    yield* skipAd()
    // 不顯示廣告與彈窗
    yield put(actions.updateIsSkippedAnnouncement(true))
    yield put(actions.updateEnableHtmlUpdatedAlert(false))
  }

  if (process.env.REACT_APP_USE_STARTUP_CACHE) {
    yield spawn(loadCachedAd)
  }

  try {
    yield call(loadTempVideoPageHost)
  } catch (err) {
    console.warn('unable to load preload data', err)
  }

  function* runWithTimeoutAndAbortMessage(
    text: string,
    timeout: number,
    generator: (signal: AbortSignal) => Generator
  ): Generator {
    try {
      const controller = new AbortController()
      const signal = controller.signal
      setTimeout(() => controller.abort(text), timeout)
      return yield* generator(signal)
    } catch (err: any) {
      // if the message is general text for some reason, throw it again
      if (err?.message === 'canceled') {
        throw Object.assign(new Error(text), { cause: err })
      } else {
        throw err
      }
    }
  }

  let station: Awaited<ReturnType<typeof fetchStation>> | undefined
  try {
    yield put(
      actions.updateMaintenanceSplash({ visible: false } as unknown as Parameters<
        typeof actions.updateMaintenanceSplash
      >[0])
    )
    yield* runWithRetry(function* () {
      let currentStation = yield* runWithTimeoutAndAbortMessage(
        '加载时间较长时，可尝试切换\n其他线路更快进入平台❤️',
        PER_REQUEST_TIMEOUT_MS,
        function* (signal: AbortSignal) {
          return (yield call(fetchStation, signal)) as Awaited<ReturnType<typeof fetchStation>>
        }
      )
      station = currentStation
      let waitTime = BASE_WAIT_TIME_MS
      let retryTime = 0

      if (currentStation?.id == null) {
        throw new Error('bad station format')
      }

      while (String(currentStation.id) !== getStationId()) {
        console.warn('API ERROR, bad site id')
        yield put(
          actions.updateRouteDetectMessage(ROUTE_ERROR_RETRY_MESSAGES[retryTime % ROUTE_ERROR_RETRY_MESSAGES.length])
        )
        // 避免上線後變成瞬間自我 ddos
        yield sleep(waitTime + (Math.random() * 2 - 1) * WAIT_TIME_RANGE)
        currentStation = yield* runWithTimeoutAndAbortMessage(
          '加载时间较长时，可尝试切换\n其他线路更快进入平台❤️',
          PER_REQUEST_TIMEOUT_MS,
          function* (signal: AbortSignal) {
            return (yield call(fetchStation, signal)) as Awaited<ReturnType<typeof fetchStation>>
          }
        )
        waitTime = Math.min(waitTime * TIME_SCALER, MAX_WAIT_TIME)
        retryTime++
      }
    })

    precachePreloadConfig(process.env.REACT_APP_PRELOAD_PATH as string, PRELOAD_DATA_LOCAL_STORAGE_KEY).catch((err) => {
      console.warn('unable to precache preload config', err)
    })

    // 站台資訊
    yield put(actions.updateStation(station as Awaited<ReturnType<typeof fetchStation>>))

    let loginResult: any = null
    yield* runWithRetry(function* () {
      // 自動使用遊客身份登入, 獲取代理碼
      const inviteCode: any = yield select((state: any) => state?.user?.inviteCode)
      const apkenc: any = yield select((state: any) => state?.user?.apkenc)
      loginResult = yield* fetchUser({ inviteCode, apkenc, noRegister: isBrowserVideoPage })
    })

    fetchDeviceQrcodeWithCache().catch((response) => {
      console.warn('failed to update device qr code', response)
    })

    if (loginResult === false && isBrowserVideoPage) {
      yield showInvalidTempTokenPage()
      return
    }

    const checkDomainTask: any = yield spawn(function* () {
      yield* runWithRetry(function* () {
        // 獲取圖片/預覽/高清用網址
        yield call(getAvailableOrigin)
      })
    })

    const loadAdTask: any = yield spawn(loadAd, checkDomainTask)

    // Non critical resource, just allow it to fail and don't retry
    yield spawn(getInformation)

    yield* runWithRetry(function* () {
      // 獲取所有 Content Management System 資訊
      const navContent: any = yield call(fetchNavigationContent)

      // Content Management System 資訊
      yield put(actions.updateNavigationContent(navContent))
    })

    try {
      yield joinWithProperErrorHandling(loadAdTask)
    } catch (err) {
      console.warn('[INIT] load ad task failed, skipping', err)
    }

    try {
      yield joinWithProperErrorHandling(checkDomainTask)
    } catch (err) {
      console.warn('[INIT] check domain task failed, skipping', err)
    }

    yield put(actions.updateReadyToEnter(true))

    yield* runWithRetry(function* () {
      try {
        // 獲取遊戲用戶狀態
        const gameUserStatus: any = yield call(fetchGameUserStatus)
        yield put(actions.updateGameUserStatus(gameUserStatus))
      } catch (ex) {
        // 檢查是不是 IP Ban，如果是的話就更新狀態
        const payload = ex as unknown as { code: number; message: string }
        if (payload.code === 460202) {
          yield put(actions.updateGameIpBan(true))
        }
        console.warn('get game user status error')
      }
    })

    setAnalyticReady()

    // getSpeedTestResults().then(reportSpeedTests)
  } catch (err) {
    yield console.error('App init Error', err)
    if (ApiError.isMaintenanceError(err)) {
      // do not capture MaintenanceError
      yield put(actions.updateRouteDetectMessage(''))
      yield put(actions.updateIsShowingLaunchSplash(false))
      yield put(
        actions.updateMaintenanceSplash({ visible: true, end: (err as any).end, message: (err as any).message })
      )
    } else if (ApiError.isForbidden(err)) {
      // do not capture MaintenanceError
      yield put(actions.updateRouteDetectMessage(''))
      yield put(actions.updateIsShowingLaunchSplash(false))
      yield put(actions.updateFullPageError({ visible: true, error: err as any }))
    } else {
      yield put(actions.updateRouteDetectMessage(''))
      yield put(actions.updateIsShowingLaunchSplash(false))
      yield put(actions.updateFullPageError({ visible: true, error: mapToUserError(err) as any }))
    }

    yield put(actions.updateReadyToShowSplashAd(false))
    yield put(actions.updateHero(mapToUserError(err)))

    setAnalyticReady()

    // getSpeedTestResults().then(reportSpeedTests)
  }
}

function processAdList(adList: SchemaOholoMaterialShow[]): Record<string, SchemaOholoMaterialShow[]> {
  const adInfo: Record<string, SchemaOholoMaterialShow[]> = {}

  Object.values(AdvertisementType).forEach((adType) => {
    const adContent = adList?.filter((info: SchemaOholoMaterialShow) => info.site_type === adType)
    adInfo[adType] = adContent
  })

  return adInfo
}

function processAnnouncementList(adInfo: any, announcementList: any[]): any[] {
  const adList = adInfo?.[AdvertisementType.Announcement]
  return [
    ...announcementList.map((item) => ({ type: AnnouncementType.System, item })),
    ...adList.map((item: any) => ({ type: AnnouncementType.Ad, item })),
  ]
}

function* getAppName(): Generator {
  yield console.log('getAppName')
}

function* getInformation(): Generator {
  try {
    const [
      // 獲取地區列表
      regionList,
      // 獲取標籤列表
      tagList,
      // 獲取短視頻主題列表
      topicList,
      // 獲取片商列表
      producerList,
    ]: any = yield all([
      call(fetchRegionList),
      call(fetchVideoTagList),
      call(fetchVideoTopicList, {}),
      call(fetchProducerList),
    ])
    yield put(actions.updateRegionList(regionList))
    yield put(actions.updateTagList(tagList))
    yield put(actions.updateTopicList(topicList.data ?? []))
    yield put(actions.updateProducerList(producerList))
  } catch (error) {
    yield console.error('getInformation Error')
  }
}

function* getAvailableOrigin(): Generator {
  // 獲取可用的 location origin
  const { domain_original, domain_preview, domain_picture, domain_comic, domain_novel, name_original }: any =
    yield call(fetchAvailableOrigin)

  yield put(
    actions.updateFullOrigins({
      domain_original,
      name_original,
      domain_preview,
      domain_picture,
      domain_comic,
      domain_novel,
    })
  )

  const mappedOrigins = name_original
    .map((name: string, index: number) => {
      if (!/^https?:\/\//.test(domain_original[index])) {
        return null
      }
      try {
        return {
          name,
          domain: new URL(domain_original[index]).origin,
        }
      } catch (err) {
        console.warn(err)
        return null
      }
    })
    .filter((i: any) => i != null)

  /** domain_picture 沒有測速 */
  yield put(actions.updatePictureOrigin(`${filterInvalidDomains(domain_picture)[0]}`))

  /** domain_comic 沒有測速 */
  yield put(actions.updateComicOrigin(`${filterInvalidDomains(domain_comic)[0]}`))

  /** domain_novel 沒有測速 */
  yield put(actions.updateNovelOrigin(`${filterInvalidDomains(domain_novel)[0]}`))

  yield put(actions.updateOfficialOriginList(mappedOrigins))

  const abortedWithErrors: any[] = []

  try {
    /** 更新預覽影片使用origin */
    const fastestPreviewOrigin: any = yield getFastestOriginWithDataVal(domain_preview)

    yield put(actions.updatePreviewOrigin(fastestPreviewOrigin))
  } catch (error) {
    console.warn('[getAvailableOrigin] fastestPreviewOrigin Error', error)
    yield put(actions.updatePreviewOrigin(filterInvalidDomains(domain_preview)[0]))
    abortedWithErrors.push(error)
  }

  try {
    /** 更新高清影片使用origin */
    const fastestOfficialOrigin: any = yield getFastestOriginWithDataVal(domain_original)

    const officialUrlInfo = new URL(fastestOfficialOrigin)
    yield put(actions.updateOfficialOrigin(officialUrlInfo.origin))
  } catch (error) {
    console.warn('[getAvailableOrigin] fastestOfficialOrigin Error', error)
    yield put(actions.updateOfficialOrigin(new URL(mappedOrigins[0].domain).origin))
    abortedWithErrors.push(error)
  }

  if (abortedWithErrors.length > 0) {
    console.warn('[getAvailableOrigin] aborted with errors', abortedWithErrors)
    throw Object.assign(new Error('请检查网络设置'), { cause: abortedWithErrors })
  }
}

function* loadCachedAd(): Generator {
  const data = localStorage.getItem(CACHED_AD_ITEM_KEY)
  try {
    if (!data) return
    const parsed = JSON.parse(data)
    yield put(actions.updateCurrentSplashAd(parsed))
    yield put(actions.updateReadyToShowSplashAd(true))
    // hide start up splash immediately
    yield put(actions.updateIsShowingLaunchSplash(false))
    console.log(parsed, 'using cached promo')
  } catch (err) {}
}

function* loadAd(checkDomainTask: any): Generator {
  // 獲取所有公告列表 和 所有廣告列表 和 Content Management System 資訊
  const [announcementList, adList, memberAnnounceList]: [
    Awaited<ReturnType<typeof fetchAnnouncement>> & { failed?: true },
    Awaited<ReturnType<typeof fetchAdList>> & { failed?: true },
    Awaited<ReturnType<typeof fetchMemberAnnounceList>>
  ] = yield all([
    call(() => fetchAnnouncement().catch(() => ({ data: [], failed: true }))),
    call(() => fetchAdList().catch(() => ({ data: [], failed: true }))),
    call(() => fetchMemberAnnounceList().catch(() => ({ data: [] }))),
  ])

  // 會員彈窗：儲存原始資料，由頁面層判斷開關
  const memberAnnounces: SchemaMemberAnnounceShow[] = memberAnnounceList?.data ?? []
  yield put(actions.updateMemberAnnounces(memberAnnounces))

  // 更新廣告資訊
  const adInfo = processAdList(adList.data)
  // 更新系統公告 和 廣告公告資訊
  const announcement = processAnnouncementList(adInfo, announcementList?.data)
  yield put(actions.updateAnnouncementList(announcement))
  yield put(actions.updateAdInfo(adInfo))

  try {
    yield joinWithProperErrorHandling(checkDomainTask)
  } catch (err) {
    console.warn('[AD] check domain task failed, skipping', err)
    const currentShowingAd: any = yield select(selectCurrentSplashAd)

    // 所有資料皆已獲取, 進入頁面
    if (currentShowingAd == null) {
      console.warn('[AD] just enter because currently without any ad')
      yield put(actions.updateReadyToShowSplashAd(true))
      yield put(actions.updateViewedAdvertisement(true))
    }

    return
  }

  // 讀取圖片
  const splashAds = adInfo[AdvertisementType.Entrance]
  /**
   * @type {import('api').SchemaOholoMaterialShow}
   */
  const selectedSplashAd = splashAds[Math.floor(splashAds.length * Math.random())]

  const pictureOrigin: any = yield select(selectPictureOrigin)

  if (!adList.failed) {
    if (process.env.NODE_ENV === 'development') {
      console.groupCollapsed('[saga/app] Selected splash ad')
      console.log(selectedSplashAd)
      console.groupEnd()
    }
    if (selectedSplashAd != null) {
      try {
        const shortcutImagePath = `${pictureOrigin}/${selectedSplashAd.icon_path}`
        const img: any = yield call(fetchBinFile, {
          url: shortcutImagePath,
        })

        const data = { item: selectedSplashAd, img }

        if (process.env.NODE_ENV === 'development') {
          console.log('Ad content fetched, updating cache')
        }

        try {
          localStorage.setItem(CACHED_AD_ITEM_KEY, JSON.stringify(data))
        } catch {
          if (process.env.NODE_ENV === 'development') {
            console.warn('unable to set ad cache')
          }
          // remove old cache
          localStorage.removeItem(CACHED_AD_ITEM_KEY)
        }

        const currentSplashAd: any = yield select(selectCurrentSplashAd)

        if (currentSplashAd == null) {
          if (process.env.NODE_ENV === 'development') {
            console.log("Using latest ad because there isn't any current displaying.")
          }
          yield put(actions.updateCurrentSplashAd(data))
          yield put(actions.updateReadyToShowSplashAd(true))
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Bad ad content, skipping update')
        }
        // just let it fail
      }
    } else {
      // actually don't have promo
      localStorage.removeItem(CACHED_AD_ITEM_KEY)
    }
  } else {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to fetch ad list, skipping update splash ad cache')
    }
  }

  const currentShowingAd: any = yield select(selectCurrentSplashAd)

  // 所有資料皆已獲取, 進入頁面
  if (currentShowingAd == null) {
    yield put(actions.updateReadyToShowSplashAd(true))
    yield put(actions.updateViewedAdvertisement(true))
  }
}

function* loadTempVideoPageHost(): Generator {
  try {
    // 檢查環境變數是否存在
    if (!process.env.REACT_APP_PRELOAD_PATH) {
      console.warn('[fau] preload not defined, using empty string')
      yield put(actions.updateTempVideoPageHost(''))
      return
    }

    const json: any = yield call(getMetadataJSONWithUrl, process.env.REACT_APP_PRELOAD_PATH)
    // speed test
    const list = filterInvalidDomains(json?.fa_urls || [])

    if (list.length === 0) {
      console.warn('[fau] empty list')
      yield put(actions.updateTempVideoPageHost(''))
      return
    }

    try {
      /** 更新瀏覽器網址 */
      const controllers: AbortController[] = []
      const probes = list.map((origin) => {
        return new Promise(async (resolve, reject) => {
          const controller = new AbortController()
          controllers.push(controller)

          const timeoutId = setTimeout(() => {
            try {
              controller.abort()
            } catch (e) {}
            reject(new Error('測速 timeout'))
          }, PER_REQUEST_TIMEOUT_MS)

          try {
            const data = await fetchBinFile({ url: origin, signal: controller.signal })
            clearTimeout(timeoutId)
            resolve([origin, data])
          } catch (err) {
            clearTimeout(timeoutId)
            reject(err)
          }
        })
      })

      try {
        const fastest: any = yield Promise.race([
          Promise.any(probes).catch(() => {
            throw Object.assign(new Error('请检查网络设置'), { title: '网络异常' })
          }),
          // 總體時間限制
          new Promise((_, reject) => setTimeout(reject, 60000)),
        ])

        // 完成時中止其他請求
        controllers.forEach((c) => {
          try {
            c.abort()
          } catch (e) {}
        })

        // fastest: [origin, data]
        console.log('[fau] url', fastest[0])
        yield put(actions.updateTempVideoPageHost(fastest[0] || ''))
        return
      } finally {
        controllers.forEach((c) => {
          try {
            c.abort()
          } catch (e) {}
        })
      }
    } catch (error) {
      console.warn('[fau] network error', error)
      yield put(actions.updateTempVideoPageHost(json?.fa_urls?.[0] || ''))
    }
  } catch (ex) {
    console.error('[fau] unable to load settings', ex)
    yield put(actions.updateTempVideoPageHost(''))
  }
}

export default function* watchApp(): Generator {
  yield takeLatest(INIT_APP, init)
  yield takeLatest(actions.initApp, getAppName)
  yield takeLatest(RACE_AVAILABLE_ORIGIN, getAvailableOrigin)
}
